/**
 * AgendaPage — `/agenda`: compromissos avulsos, ordens de serviço agendadas e
 * vencimentos do financeiro num calendário só.
 *
 * Módulo GRÁTIS. O bloco de vencimentos tem gate de PAPEL (owner/admin), porque
 * `finance_entries` é owner/admin na RLS — um `member` não pode ver o dinheiro
 * por uma porta lateral (#38).
 *
 * ─── O DESENHO OFICIAL NUMA TELA QUE NÃO É LISTA ────────────────────────────
 *
 * Hero sangrado + bilhete, o mesmo de `/quotes`, `/clients`, `/catalog`,
 * `/trabalhos` e `/ordens-servico`. Esta é a primeira que não é lista nem
 * formulário, e por isso três peças precisaram ser TRADUZIDAS (o exercício do
 * #182/#188/#195/#197):
 *
 * · **O VALOR AO VIVO é a CONTAGEM DO DIA SELECIONADO**, não dinheiro. O
 *   porquê está inteiro no cabeçalho de `src/lib/agendaSummary.ts`, e resumido
 *   é: o dia mistura entrada e saída (somar ou compensar mentiria dos dois
 *   jeitos) e dinheiro aqui tem gate de papel, então para um `member` o hero
 *   anunciaria R$ 0,00 com a agenda cheia (#187). A contagem sai dos eventos
 *   JÁ VISÍVEIS — depois do papel e do alternador —, então ela nunca fala de
 *   algo que a tela não mostra.
 * · **O RÓTULO do valor é a DATA**, e é ele que torna a contagem honesta: um
 *   "3" sozinho não diz de que dia se está falando. "Hoje"/"Amanhã"/"Ontem"
 *   entram na frente quando cabem, porque é assim que a pessoa pensa no próprio
 *   dia. A sublinha diz a COMPOSIÇÃO (compromissos · serviços · vencimentos),
 *   que é o que separa três visitas de três boletos.
 * · **O RECORTE desta tela é o CALENDÁRIO.** Por isso ela não ganhou busca,
 *   ordenação nem faixa de filtros: quem escolhe o que aparece é o dia clicado,
 *   e uma barra de controles duplicaria o papel do calendário logo abaixo dela.
 *   Pelo mesmo motivo não há faixa de chips por tipo de evento — as contagens
 *   por tipo já estão na sublinha do hero, e o alternador Compromissos/Com
 *   vencimentos já é a troca de contexto da tela (#25).
 * · **O BOTÃO DE VOLTAR fica**: `/agenda` NÃO é destino da navegação inferior
 *   (Início, Orçamentos, Novo, Clientes, Catálogo), então sem ele não há saída
 *   barata da tela.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  Plus,
  CalendarDays,
  ClipboardList,
  Clock,
  DollarSign,
  MapPin,
  Cake,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useCalendarEvents, APPOINTMENT_STATUSES } from '@/hooks/useAppointments';
import { useRole } from '@/hooks/useRole';
import { dayKeyOf, type CalendarEvent } from '@/lib/calendarEvents';
import { SERVICE_ORDER_STATUSES, type ServiceOrderStatus } from '@/hooks/useServiceOrders';
import { reconcileAppointmentReminders } from '@/lib/appointmentReminders';
import { formatBRL, STATUS_LABELS } from '@/lib/financeLabels';
import { agendaDayBreakdown, agendaDayRelativeLabel, summarizeAgendaDay } from '@/lib/agendaSummary';
import { CONTENT_CARD } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import type { FinanceStatus } from '@/types/database';
import { companyWeekStartsOn } from '@/lib/reportPeriod';

const SO_STATUS_STYLES: Record<ServiceOrderStatus, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
  cancelled: 'bg-muted text-muted-foreground',
};

const APPT_STATUS_STYLES: Record<string, string> = {
  scheduled: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400',
  done: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
  cancelled: 'bg-muted text-muted-foreground',
};

const FINANCE_STATUS_STYLES: Record<FinanceStatus, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
  canceled: 'bg-muted text-muted-foreground',
};

function statusLabel(event: CalendarEvent): string {
  // Aniversario nao tem STATUS: nao ha o que concluir nem cancelar. O rotulo
  // e o proprio tipo — usar o vocabulario do compromisso ("Agendado") mentiria.
  if (event.kind === 'birthday') return 'Aniversário';
  if (event.kind === 'service_order') {
    return SERVICE_ORDER_STATUSES.find((s) => s.value === event.status)?.label ?? event.status;
  }
  if (event.kind === 'finance_entry') {
    return STATUS_LABELS[event.status as FinanceStatus] ?? event.status;
  }
  return APPOINTMENT_STATUSES.find((s) => s.value === event.status)?.label ?? event.status;
}

function statusStyle(event: CalendarEvent): string {
  if (event.kind === 'birthday') {
    return 'bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-400';
  }
  if (event.kind === 'service_order') {
    return SO_STATUS_STYLES[event.status as ServiceOrderStatus] ?? 'bg-muted text-muted-foreground';
  }
  if (event.kind === 'finance_entry') {
    return FINANCE_STATUS_STYLES[event.status as FinanceStatus] ?? 'bg-muted text-muted-foreground';
  }
  return APPT_STATUS_STYLES[event.status] ?? 'bg-muted text-muted-foreground';
}

/** Parse a 'YYYY-MM-DD' day key into a local Date at midnight. */
function dateFromDayKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export default function AgendaPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { canSeeFinance } = useRole();
  const queryClient = useQueryClient();

  /* O mes visivel do calendario. Declarado ANTES do hook de eventos porque e
     ele que decide em que ano os aniversarios sao projetados — navegar para
     2027 sem isso mostraria a grade sem aniversario nenhum (corte silencioso). */
  const [month, setMonth] = useState<Date>(() => new Date());
  const { events, appointments } = useCalendarEvents(month.getFullYear());

  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());

  const [showFinanceEntries, setShowFinanceEntries] = useState(true);

  const visibleEvents = useMemo(
    () =>
      canSeeFinance && showFinanceEntries
        ? events
        : events.filter((event) => event.kind !== 'finance_entry'),
    [events, canSeeFinance, showFinanceEntries],
  );

  // Group events by local calendar day (YYYY-MM-DD).
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of visibleEvents) {
      const key = dayKeyOf(ev.date);
      const list = map.get(key) ?? [];
      list.push(ev);
      map.set(key, list);
    }
    return map;
  }, [visibleEvents]);

  // Separate calendar markers make payment deadlines distinguishable from
  // appointments/OS, including days that contain both kinds.
  const markerDays = useMemo(
    () => {
      const scheduleOnly: Date[] = [];
      const financeOnly: Date[] = [];
      const mixed: Date[] = [];
      for (const [key, dayEvents] of eventsByDay) {
        const hasFinance = dayEvents.some((event) => event.kind === 'finance_entry');
        const hasSchedule = dayEvents.some((event) => event.kind !== 'finance_entry');
        const date = dateFromDayKey(key);
        if (hasFinance && hasSchedule) mixed.push(date);
        else if (hasFinance) financeOnly.push(date);
        else scheduleOnly.push(date);
      }
      return { scheduleOnly, financeOnly, mixed };
    },
    [eventsByDay],
  );

  const selectedKey = dayKeyOf(selectedDate);
  const dayEvents = eventsByDay.get(selectedKey) ?? [];

  /**
   * O RESUMO DO HERO. Sai de `dayEvents`, que já passou pelo gate de papel e
   * pelo alternador de vencimentos — o número nunca fala de algo que a tela não
   * mostra. A regra de contagem mora no módulo puro `agendaSummary`.
   */
  const daySummary = summarizeAgendaDay(dayEvents);
  const dayBreakdown = agendaDayBreakdown(daySummary);
  const relativeDayLabel = agendaDayRelativeLabel(selectedDate);
  const longDayLabel = format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR });

  // Refresh when an offline write (or another tab / a finished sync) touches
  // agenda data — appointments, service orders and finance due dates.
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['appointments', activeCompany?.id] });
      queryClient.invalidateQueries({ queryKey: ['service-orders', activeCompany?.id] });
      queryClient.invalidateQueries({ queryKey: ['finance-entries', activeCompany?.id] });
    };
    window.addEventListener('appointments-updated', handler);
    window.addEventListener('service-order-updated', handler);
    window.addEventListener('finance-updated', handler);
    return () => {
      window.removeEventListener('appointments-updated', handler);
      window.removeEventListener('service-order-updated', handler);
      window.removeEventListener('finance-updated', handler);
    };
  }, [queryClient, activeCompany?.id]);

  // Reconcile local reminders whenever the appointment list settles (native
  // only; web no-op). Deterministic ids mean offline→UUID reconciles itself.
  useEffect(() => {
    void reconcileAppointmentReminders(appointments as any);
  }, [appointments]);

  /**
   * O dia selecionado no calendário viaja na URL (`?data=`) em vez do state da
   * navegação: é um dado simples, e assim ele sobrevive a recarregar a página.
   */
  const openNew = () => {
    navigate(`/agenda/novo?data=${selectedKey}`);
  };

  const handleEventClick = (event: CalendarEvent) => {
    // O aniversario leva ao CLIENTE — e o unico destino acionavel: dali se
    // manda a mensagem. Um evento que nao abre nada seria ruido no calendario.
    if (event.kind === 'birthday') {
      navigate(`/clients/${event.ref.id}`);
      return;
    }
    if (event.kind === 'service_order') {
      navigate(`/ordens-servico/${event.ref.id}`);
      return;
    }
    if (event.kind === 'finance_entry') {
      navigate('/financeiro');
      return;
    }
    navigate(`/agenda/${event.ref.id}/editar`);
  };

  if (!user) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <CalendarDays className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Agenda</h2>
          <p className="mb-4 text-muted-foreground">Faça login para acessar a agenda</p>
          <Button3D onClick={() => navigate('/auth')}>Entrar</Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app                                     */}
        {/* ================================================================== */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => navigate('/dashboard')}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Rotina</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">Agenda</h1>
            </div>
            {/* A ação PRIMÁRIA fica com o rótulo CURTO: "Novo compromisso" na
                linha do título não cabe em 360px, e sem `flex-wrap` o excedente
                não quebra — ele sai da tela (#186). O nome inteiro vai no
                `aria-label`/`title`, que é o que o leitor de tela anuncia
                (#142). */}
            <Button3D
              size="sm"
              color="light"
              className="shrink-0"
              onClick={openNew}
              aria-label="Novo compromisso"
              title="Novo compromisso"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Novo
            </Button3D>
          </div>

          <div className="relative mt-5 min-w-0">
            {/* O rótulo do valor é a DATA do dia selecionado — sem ele, a
                contagem não diz de que dia está falando. `first-letter:uppercase`
                porque o `date-fns` em pt-BR devolve o dia da semana em
                minúsculas. */}
            <p className="text-xs text-white/75 first-letter:uppercase">
              {relativeDayLabel ? `${relativeDayLabel} · ${longDayLabel}` : longDayLabel}
            </p>
            <p className="truncate text-3xl font-bold leading-tight">{daySummary.count}</p>
            <p className="mt-0.5 text-xs text-white/70">
              {daySummary.count === 0
                ? canSeeFinance && showFinanceEntries
                  ? 'Nenhum compromisso ou vencimento neste dia'
                  : 'Nenhum compromisso neste dia'
                : dayBreakdown}
            </p>
          </div>
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes.
            No DESKTOP vira calendário à esquerda e o dia selecionado à direita.
            O comentário do bloco da lista explica por que ela repete a data no
            celular: "o calendário é alto, então o hero já rolou para fora da
            tela quando esta lista aparece". Lado a lado, esse motivo deixa de
            existir — a lista fica na primeira dobra, que é o que se espera de
            uma agenda em computador. */}
        <div className="relative z-10 -mt-9 space-y-5 lg:grid lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] lg:items-start lg:gap-5 lg:space-y-0">
          {/* O alternador é a única troca de CONTEXTO da tela, e por isso segue
              sendo `<Tabs>` azul herdado do componente base (#25). */}
          {canSeeFinance && (
            <Tabs
              className="lg:col-span-2"
              value={showFinanceEntries ? 'payments' : 'agenda'}
              onValueChange={(value) => setShowFinanceEntries(value === 'payments')}
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="agenda">Compromissos</TabsTrigger>
                <TabsTrigger value="payments">Com vencimentos</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* Calendário + legenda são um grupo só (o calendário marca, a legenda
              explica a marca), então ficam com `space-y-2` dentro do `space-y-5`
              que separa as SEÇÕES (#191/#194). */}
          <div className="space-y-2">
            <div className={cn(CONTENT_CARD, 'flex justify-center p-2 sm:p-3')}>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                month={month}
                onMonthChange={setMonth}
                locale={ptBR}
                weekStartsOn={companyWeekStartsOn(activeCompany)}
                modifiers={{
                  scheduleOnly: markerDays.scheduleOnly,
                  financeOnly: markerDays.financeOnly,
                  mixed: markerDays.mixed,
                }}
                modifiersClassNames={{
                  scheduleOnly:
                    'relative font-semibold after:absolute after:bottom-1 after:left-1/2 after:h-1.5 after:w-1.5 after:-translate-x-1/2 after:rounded-full after:bg-sky-500 after:content-[""]',
                  financeOnly:
                    'relative font-semibold after:absolute after:bottom-1 after:left-1/2 after:h-1.5 after:w-1.5 after:-translate-x-1/2 after:rounded-full after:bg-amber-500 after:content-[""]',
                  mixed:
                    'relative font-semibold after:absolute after:bottom-1 after:left-[42%] after:h-1.5 after:w-1.5 after:-translate-x-1/2 after:rounded-full after:bg-sky-500 after:content-[""] before:absolute before:bottom-1 before:left-[58%] before:h-1.5 before:w-1.5 before:-translate-x-1/2 before:rounded-full before:bg-amber-500 before:content-[""]',
                }}
              />
            </div>

            {/*
              LEGENDA das bolinhas.

              O calendário marcava os dias com um ponto azul, um âmbar ou os dois
              — e nada em lugar nenhum da tela dizia o que cada cor significa. Um
              indicador sem legenda não é informação: é enfeite que o usuário
              tenta decifrar.

              A bolinha âmbar só entra quando os vencimentos estão de fato em
              jogo (papel + alternador), senão a legenda prometeria uma marca que
              aquele usuário nunca vai ver.
            */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-sky-500" aria-hidden="true" />
                Compromisso ou serviço
              </span>
              {canSeeFinance && showFinanceEntries && (
                <span className="flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
                  Vencimento
                </span>
              )}
            </div>
          </div>

          {/* O dia selecionado. O título REPETE a data que está no hero de
              propósito: o calendário é alto, então o hero já rolou para fora da
              tela quando esta lista aparece — é o mesmo arranjo do total no card
              de fechamento do PDV (#197). */}
          <div className="space-y-2">
            <h2 className="px-1 text-sm font-medium text-muted-foreground first-letter:uppercase">
              {longDayLabel}
            </h2>

            {dayEvents.length === 0 ? (
              <div
                className={cn(
                  CONTENT_CARD,
                  'flex flex-col items-center justify-center border-dashed py-8 text-center',
                )}
              >
                <CalendarDays className="mb-3 h-12 w-12 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  {showFinanceEntries && canSeeFinance
                    ? 'Nenhum compromisso ou vencimento neste dia'
                    : 'Nenhum compromisso neste dia'}
                </p>
                <Button variant="outline" size="sm" className="mt-4" onClick={openNew}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  Adicionar compromisso
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {dayEvents.map((event) => (
                  <div
                    key={`${event.kind}-${event.id}`}
                    className={cn(
                      CONTENT_CARD,
                      'flex cursor-pointer items-start gap-3 p-3 transition-shadow hover:shadow-md',
                      event.kind === 'finance_entry' &&
                        (event.status === 'paid'
                          ? 'border-l-4 border-l-emerald-400 bg-muted/30'
                          : event.ref?.kind === 'expense'
                            ? 'border-l-4 border-l-red-400'
                            : 'border-l-4 border-l-emerald-500'),
                    )}
                    onClick={() => handleEventClick(event)}
                  >
                    <div className="flex w-16 shrink-0 flex-col items-center justify-center rounded-md bg-muted py-2">
                      {event.kind === 'birthday' ? (
                        <>
                          <Cake className="mb-0.5 h-4 w-4 text-pink-600" />
                          <span className="text-center text-[10px] font-medium leading-tight text-muted-foreground">
                            Aniversário
                          </span>
                        </>
                      ) : event.kind === 'finance_entry' ? (
                        <>
                          <DollarSign className="mb-0.5 h-4 w-4 text-amber-600" />
                          <span className="text-center text-[10px] font-medium leading-tight text-muted-foreground">
                            Vencimento
                          </span>
                        </>
                      ) : event.allDay ? (
                        <span className="text-center text-[11px] font-medium leading-tight text-muted-foreground">
                          Dia inteiro
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-sm font-semibold">
                          <Clock className="h-3 w-3" />
                          {format(event.date, 'HH:mm')}
                        </span>
                      )}
                    </div>

                    {/* `min-w-0` não é decoração (#175): sem ele um flex item
                        nasce com `min-width: auto` e um título sem espaços
                        estica a linha para fora da viewport. */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2">
                        {/* Sem `truncate`: é o título que distingue duas linhas
                            do mesmo dia, e cortá-lo come justamente o que
                            informa (#175). */}
                        <p className="min-w-0 break-words font-medium">{event.title}</p>
                        {event.ref?._offline_pending && (
                          <Badge variant="outline" className="shrink-0 text-[10px]">
                            Offline
                          </Badge>
                        )}
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Badge variant="secondary" className="gap-1 text-[10px] font-normal">
                          {event.kind === 'service_order' ? (
                            <>
                              <ClipboardList className="h-3 w-3" />
                              OS
                            </>
                          ) : event.kind === 'finance_entry' ? (
                            <>
                              <DollarSign className="h-3 w-3" />
                              {event.ref?.kind === 'expense' ? 'A pagar' : 'A receber'}
                            </>
                          ) : (
                            <>
                              <CalendarDays className="h-3 w-3" />
                              Compromisso
                            </>
                          )}
                        </Badge>
                        <Badge className={cn('border-0 text-[10px]', statusStyle(event))}>
                          {statusLabel(event)}
                        </Badge>
                        {event.kind === 'appointment' && event.ref?.location && (
                          <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="truncate">{event.ref.location}</span>
                          </span>
                        )}
                      </div>

                      {event.kind === 'finance_entry' && (
                        <p
                          className={cn(
                            'mt-2 text-base font-semibold',
                            event.ref?.kind === 'expense' ? 'text-red-600' : 'text-emerald-600',
                          )}
                        >
                          {formatBRL(Number(event.ref?.amount ?? 0))}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
